import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import CloseBig from 'assets/icons/CloseBig';
import MaxWidthWrapper from 'components/wrappers/MaxWidthWrapper';
import SearchContainer from 'containers/SearchContainer';
import SearchContent from 'Header/Default/Search/SearchContent';
import SearchInput from 'components/pages/search/SearchInput';
import { TextLinkWysisyg } from 'components/text/TextLink';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import { above } from 'utils/mediaqueries';
import colors from 'config/theme/colors';
import sanitizeUserInput from 'utils/sanitizeUserInput';
import searchAndReplaceWysiwyg from 'libs/wordpress/utils/searchAndReplaceWysiwyg';
import styled from 'libs/styled';
import { useSelector } from 'react-redux';

const InputWrapper = styled('div')`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 0;

    ${above.md} {
        padding: 24px 0 18px;
    }
`;

const StyledSearchInput = styled(SearchInput)`
    font-size: 2.4rem;
    line-height: 1;

    ${above.md} {
        font-size: 6.4rem;
    }
`;

const NoResultWrapper = styled('div')`
    position: relative;
    padding: 12px 0px;

    ${above.md} {
        padding: 24px 0px;
    }

    ::before {
        display: block;
        content: '';
        position: absolute;
        top: 0px;
        left: 50%;
        width: 100vw;
        border-top: 1px solid ${colors.grey4};
        transform: translateX(-50%);
    }
`;

const Text = styled('p')`
    color: ${colors.error};
    font-size: 16px;
    line-height: 26px;

    a {
        color: inherit;
    }
`;

const Search = () => {
    const hideOverlay = useSelector(state => state.overlay.hide);
    const searchConfig = useSelector(state => state.header.data.search) || {};
    const { placeholder, noResultText } = searchConfig;
    const history = useHistory();
    const { search } = useLocation();
    const params = new URLSearchParams(search);

    const [searchstring, setSearchstring] = useState(null);

    // Replace {search} with the searchstring
    const replacedNoResultText = searchAndReplaceWysiwyg(noResultText, '{search}', searchstring);

    const handleKeyup = e => {
        // ESC
        if (e.keyCode === 27) {
            hideOverlay();
        }
    };

    useEffect(() => {
        document.addEventListener('keyup', handleKeyup);
        return () => {
            document.removeEventListener('keyup', handleKeyup);
        };
    }, []);

    useEffect(() => {
        // Update search value in url
        const string = sanitizeUserInput(searchstring, 100);
        if (string) {
            params.set('s', encodeURI(string));
            history.push({
                search: params.toString(),
            });
        }
    }, [searchstring]);

    return (
        <>
            <MaxWidthWrapper includeContentMargins>
                <InputWrapper>
                    <StyledSearchInput
                        focusOnMount
                        singleRow
                        editTextBottomPosition={['14%', null, null, '19%']}
                        handleSubmit={setSearchstring}
                        placeholderText={placeholder}
                        updateSearch={setSearchstring}
                    />
                    <CloseBig
                        cursor="pointer"
                        height={['24px', null, null, '48px']}
                        width={['32px', null, null, '64px']}
                        color={colors.grey4}
                        onClick={() => hideOverlay()}
                    />
                </InputWrapper>
            </MaxWidthWrapper>
            {!!searchstring && (
                <SearchContainer
                    pageSize={8}
                    search={searchstring}
                    render={({ response }) => {
                        if (response?.hitsCount > 0) {
                            return (
                                <SearchContent
                                    products={response.products}
                                    hitsCount={response.hitsCount}
                                    fallbackProducts={response.fallbackProducts}
                                    categories={response.categories}
                                    searchString={searchstring}
                                />
                            );
                        }

                        if (response?.hitsCount === 0) {
                            return (
                                <MaxWidthWrapper includeContentMargins>
                                    <NoResultWrapper>
                                        <Wysiwyg
                                            data={replacedNoResultText}
                                            textComponent={props => <Text {...props} />}
                                            tagComponents={{ url: TextLinkWysisyg }}
                                        />
                                    </NoResultWrapper>
                                </MaxWidthWrapper>
                            );
                        }

                        return null;
                    }}
                />
            )}
        </>
    );
};

export default Search;
